/**
 * 이상한 방 - 준비물(kit)
 *
 * 근거 문서: 로드맵 확정안 3.2절(준비물은 넷뿐이다) · 3.6절(페이월 금지) · 4절 3번(로그라이크 3택1의 자리)
 *           `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제) · 7절(플레이테스트 수치)
 *
 * ## 준비물은 튜닝을 "늘리는 방향으로만" 건드린다
 *
 * 봉투에서 나오는 것 중 라운드에 실제로 영향을 주는 것은 준비물 하나다. 그래서 이 파일의 존재 이유는
 * 무엇을 주는가가 아니라 **무엇을 줄 수 없는가**를 타입과 검사로 못박는 것이다.
 *
 *  - `KitTuning`은 `Tuning`의 네 필드만 고른 화이트리스트다. `changeCount`(이상한 곳 수)·
 *    `allowedVerbs`(동사)·`originCooldownSeconds`(깜빡비교의 유일한 방어선)·`warpDissolveSeconds`는
 *    **타입에 없다.** 누가 "쿨타임 줄이는 준비물"을 만들려 하면 컴파일이 먼저 막는다.
 *    (`economy.test.ts`가 타입 테스트로 고정한다.)
 *  - 네 값은 전부 **관찰을 늘리거나 실수 여유를 주는 방향**이다. 외우기 +15초는 더 외우게 하고,
 *    체류 +10초는 더 대조하게 하고, 수색 +30초는 덜 조급하게 하고, 탄환 +2는 두 번 더 틀려도 되게 한다.
 *    속도를 사는 것(힌트·자동 조준·쿨타임 단축)은 하나도 없다 - 그건 최적 전략을 "관찰을 멈추고
 *    빨리 쏘기"로 뒤집는다(`combo.ts` 머리말과 같은 이유).
 *  - `validateKits`가 "모든 보너스가 0보다 크다"를 검사한다. 줄이는 준비물은 데이터로도 못 들어온다.
 *
 * ## 사람 매치에는 자리가 없다
 *
 * `createMatch`가 준비물을 받지 않는다(로드맵 2.2절). 뽑기를 많이 한 사람이 여럿이 판에서 이기면
 * 그 순간 봉투가 페이투윈이 된다. 준비물은 단계·자유 모드의 **단계 안내 화면 3택1**(M9)에서만
 * 하나를 챙긴다 - 라운드 **전** 선택이라 라운드 중 판단을 바꾸지 않는다.
 *
 * ## 탄창 0 클리어 불변식은 준비물 0 기준이다
 *
 * `levels.test.ts`의 "탄창 없이 전 단계 클리어"는 준비물이 하나도 없는 사람 기준으로 그대로 통과해야 한다.
 * 준비물이 있어야 깰 수 있는 단계가 하나라도 생기면 그게 페이월이다. 이 파일은 그 불변식을 건드릴
 * 방법이 없다 - 단계 표(`levels.ts`)를 읽지도 않고, 튜닝에 **더하기만** 한다.
 */

import type { Tuning } from '../tuning'

/* --- 타입 ------------------------------------------------------------------- */

/** 준비물 넷. 순서는 안내 화면의 기본 나열 순서다(짧은 것부터). */
export const KIT_IDS = ['magnifier', 'leisure', 'slowClock', 'spare'] as const

export type KitId = (typeof KIT_IDS)[number]

/**
 * 준비물이 건드릴 수 있는 튜닝 필드 **전부**. 여기 없는 필드는 준비물이 만질 수 없다.
 *
 * `Pick`으로 좁힌 것이 규칙의 표현이다. `Partial<Tuning>`으로 두면 "쿨타임 -3초"가 타입으로는
 * 합법이고, 그걸 막는 코드가 검증기 한 곳에만 남는다. 타입이 막으면 검증기는 두 번째 방어선이 된다.
 */
export type KitTuning = Pick<Tuning, 'watchSeconds' | 'huntSeconds' | 'originStaySeconds' | 'startAmmo'>

/** 준비물 하나. 문구는 i18n 키로만 가리킨다 - 데이터에 한국어를 두지 않는다(GLOSSARY). */
export type KitDef = Readonly<{
  id: KitId
  nameKey: `kit.${KitId}.name`
  descKey: `kit.${KitId}.desc`
  /**
   * 기본 튜닝 위에 **더하는** 양. 곱하지 않는 이유는 단계마다 기본값이 달라(1단계 외우기 30초,
   * 10단계 60초) 배수면 후반이 더 큰 혜택을 받기 때문이다. "외우기 15초"는 어느 단계에서나 15초다.
   */
  bonus: Readonly<Partial<KitTuning>>
}>

/* --- 데이터 ------------------------------------------------------------------ */

/**
 * 준비물 넷. **늘리지 마라.** 로드맵 6절이 '가벼운 문'(쿨타임 하향)·'탄피 회수'(오답 환급)·힌트류를
 * 전부 기각했고, 그 이유는 셋 다 관찰 축을 무너뜨리거나 리듀서에 훅을 요구하기 때문이다.
 * 새 준비물이 필요하다고 느껴지면 그 전에 `KitTuning`에 필드를 더할 수 있는지부터 물어라 -
 * 대부분은 더할 수 없고, 그러면 그건 준비물이 아니다.
 */
export const KITS: readonly KitDef[] = [
  /** 외우기를 15초 늘린다. 기준 기억이 늘어나는 것이라 이 게임의 축(관찰)을 그대로 밀어 준다. */
  { id: 'magnifier', nameKey: 'kit.magnifier.name', descKey: 'kit.magnifier.desc', bonus: { watchSeconds: 15 } },
  /** 원래 방 체류를 10초 늘린다. 쿨타임은 건드리지 않는다 - 대조 시간이 늘어나지 왕복 횟수가 늘지는 않는다. */
  { id: 'leisure', nameKey: 'kit.leisure.name', descKey: 'kit.leisure.desc', bonus: { originStaySeconds: 10 } },
  /** 수색 상한을 30초 늘린다. 다 찾으면 조기 종료하므로 잘하는 사람에게는 아무 의미가 없다. */
  { id: 'slowClock', nameKey: 'kit.slowClock.name', descKey: 'kit.slowClock.desc', bonus: { huntSeconds: 30 } },
  /**
   * 시작 탄환을 2발 늘린다. `startAmmo`는 탄환 상한이기도 해서(`gameState.ammoCapacity`) 상한도
   * 함께 오른다 - 그래야 탄창을 넣을 때 "여분 때문에 자리가 없다"가 생기지 않는다.
   */
  { id: 'spare', nameKey: 'kit.spare.name', descKey: 'kit.spare.desc', bonus: { startAmmo: 2 } },
]

/** id → 정의. 저장값·봉투 산출물에서 온 id를 찾는 자리다. */
const KIT_BY_ID: ReadonlyMap<KitId, KitDef> = new Map(KITS.map((kit) => [kit.id, kit]))

/* --- 조회 -------------------------------------------------------------------- */

export function isKitId(value: unknown): value is KitId {
  return typeof value === 'string' && (KIT_IDS as readonly string[]).includes(value)
}

export function getKit(id: KitId): KitDef {
  const kit = KIT_BY_ID.get(id)
  // `KitId`는 목록에서 파생된 유니온이라 여기 도달할 수 없다. 형식 서술이다(`looks.getLook`·`titles.getNickname`과 같은 모양).
  if (kit === undefined) throw new RangeError(`준비물 '${id}'가 없다`)
  return kit
}

/* --- 적용 -------------------------------------------------------------------- */

/**
 * 준비물을 튜닝의 네 필드에 얹는다. **더하기만** 한다.
 *
 * 입력 `t`가 `KitTuning`인 것은 이 함수가 그 넷 밖의 값을 읽을 수도 쓸 수도 없다는 뜻이다.
 * 완전한 `Tuning`이 필요하면 `withKit`을 써라 - 그쪽이 나머지 필드를 그대로 흘려보낸다.
 */
export function applyKit(kit: KitDef, t: KitTuning): KitTuning {
  return {
    watchSeconds: t.watchSeconds + (kit.bonus.watchSeconds ?? 0),
    huntSeconds: t.huntSeconds + (kit.bonus.huntSeconds ?? 0),
    originStaySeconds: t.originStaySeconds + (kit.bonus.originStaySeconds ?? 0),
    startAmmo: t.startAmmo + (kit.bonus.startAmmo ?? 0),
  }
}

/**
 * 단계 안내 화면이 고른 준비물을 라운드 튜닝에 얹는다. `null`이면 그대로다(준비물 없이 들어가는 사람).
 *
 * 스프레드 순서가 규칙이다 - `applyKit`의 결과가 뒤에 오므로 네 필드만 바뀌고, 그 넷은
 * `KitTuning`이 허락한 것뿐이다. 이 함수가 `Tuning`의 다른 필드를 바꿀 방법은 타입상 없다.
 */
export function withKit(tuning: Tuning, kit: KitDef | null): Tuning {
  if (kit === null) return tuning
  return { ...tuning, ...applyKit(kit, tuning) }
}

/* --- 검증 -------------------------------------------------------------------- */

/**
 * 준비물 표를 검사한다. 문제가 없으면 빈 배열(`levels.validateLevels`와 같은 모양).
 *
 * "늘리는 방향만"이 핵심 규칙이다. 0이나 음수 보너스는 준비물이 아니라 벌칙이고, 벌칙을 뽑기에서
 * 주는 순간 "가치 0인 봉투 없음"(로드맵 3.2절)이 깨진다. 빈 보너스도 같은 이유로 거절한다.
 */
export function validateKits(kits: readonly KitDef[] = KITS): readonly string[] {
  const issues: string[] = []
  const seen = new Set<KitId>()

  for (const kit of kits) {
    if (seen.has(kit.id)) issues.push(`준비물 id '${kit.id}'가 겹친다`)
    seen.add(kit.id)

    if (kit.nameKey !== `kit.${kit.id}.name`) issues.push(`'${kit.id}'의 이름 키가 id와 어긋난다: ${kit.nameKey}`)
    if (kit.descKey !== `kit.${kit.id}.desc`) issues.push(`'${kit.id}'의 설명 키가 id와 어긋난다: ${kit.descKey}`)

    const entries = Object.entries(kit.bonus) as readonly (readonly [keyof KitTuning, number | undefined])[]
    if (entries.length === 0) issues.push(`'${kit.id}'는 아무것도 늘리지 않는다 - 가치 0인 산출물이다`)

    for (const [field, amount] of entries) {
      if (amount === undefined) continue
      if (!Number.isFinite(amount) || amount <= 0) {
        issues.push(`'${kit.id}'의 ${field}이(가) ${String(amount)}다 - 준비물은 늘리는 방향만 허용한다`)
      }
    }
  }

  if (seen.size !== KIT_IDS.length) issues.push(`준비물이 ${String(KIT_IDS.length)}개여야 하는데 ${String(seen.size)}개다`)

  return issues
}
